import { createTheme } from "@mui/material/styles";

export const theme = createTheme({
  palette: {
    primary: {
      main: "#e6007e",
      light: "#ff5fb0",
    },
    secondary: {
      main: "#444",
      light: "#ccc",
      dark: "#111",
    },
    error: {
      main: "#d32f2f",
      light: "#f4cbcb",
      dark: "#9e2323",
    },
    warning: {
      main: "#ef6c00",
      light: "#fbdabf",
      dark: "#b35100",
    },
    info: {
      main: "#3996DC",
      light: "#cde4f6",
      dark: "#2a70a5",
    },
    success: {
      main: "#2e7d32",
      light: "#cadecb",
      dark: "#225d25",
    },
    common: {
      black: "#000",
      white: "#fff",
    },
    background: {
      default: "#fff",
      paper: "#fff",
    },
  },
  typography: {
    fontFamily: "Open Sans, sans-serif",
    fontSize: 14,
    htmlFontSize: 20,
    h1: {
      fontSize: "var(--font-size-h1)",
      fontWeight: 500,
      lineHeight: "2rem",
      letterSpacing: 0.02,
    },
    h2: {
      fontSize: "var(--font-size-h2)",
      fontWeight: 500,
      lineHeight: "1.5rem",
      letterSpacing: 0.02,
    },
    h3: {
      fontSize: "var(--font-size-h3)",
      fontWeight: 500,
      lineHeight: "1.5rem",
      letterSpacing: 0.02,
    },
    h4: {
      fontSize: "var(--font-size-h4)",
      fontWeight: 500,
      lineHeight: "1.5rem",
      letterSpacing: 0.02,
    },
    h5: {
      fontSize: "var(--font-size-h5)",
      fontWeight: 500,
      lineHeight: "1rem",
      letterSpacing: 0.02,
    },
    h6: {
      fontSize: "var(--font-size-h6)",
      fontWeight: 500,
      lineHeight: "1rem",
      letterSpacing: 0.02,
    },
    subtitle1: {
      fontSize: "var(--font-size-xl)",
      fontWeight: 500,
      lineHeight: "1rem",
      letterSpacing: 0,
    },
    subtitle2: {
      fontSize: "var(--font-size-m)",
      fontWeight: 500,
      lineHeight: "1rem",
      letterSpacing: 0,
    },
    caption: {
      fontSize: "var(--font-size-s)",
      fontWeight: 600,
      lineHeight: "1rem",
      letterSpacing: 0.1,
    },
    overline: {
      fontSize: "var(--font-size-s)",
      fontWeight: 600,
      lineHeight: "1rem",
      textTransform: "uppercase",
      letterSpacing: 0.1,
    },
    body1: {
      fontSize: "var(--font-size-m)",
      lineHeight: "1rem",
      letterSpacing: 0,
      color: "inherit",
    },
    body2: {
      fontSize: "var(--font-size-s)",
      fontWeight: "inherit",
      lineHeight: "1rem",
      color: "inherit",
    },
    button: {
      fontSize: "var(--font-size-m)",
      fontWeight: 600,
      lineHeight: "1rem",
      letterSpacing: 0.02,
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          subtitle1: "h3",
          subtitle2: "h4",
          body1: "p",
          body2: "span",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "medium",
        margin: "dense",
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "var(--font-size-s)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "var(--font-size-m)",
        },
        sizeLarge: {
          height: "2.5rem",
          fontSize: "var(--font-size-l)",
        },
        sizeMedium: {
          height: "2rem",
          fontSize: "var(--font-size-m)",
        },
        sizeSmall: {
          height: "1.5rem",
          fontSize: "var(--font-size-s)",
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          margin: "0.5rem 0 0.25rem 0",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          lineHeight: "1rem",
          height: "2.5rem",
        },
        sizeSmall: {
          height: "2rem",
        },
        input: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: "0.75rem",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: "-0.5rem",
          marginRight: "1rem",
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: "8px 0.5rem",
          marginRight: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: "0.25rem 0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "0.75rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "1rem 1rem 0 1rem",
          fontSize: "var(--font-size-h4)",
          fontWeight: 500,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "1rem !important",
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: "var(--color-black)",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: "0.5rem",
        },
        spacing: {
          padding: "0 1rem 1rem 1rem",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          boxShadow:
            "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
        },
        message: {
          fontSize: "var(--font-size-m)",
        },
      },
    },
  },
});
